import React from 'react';
import { Loader2, Check, ShoppingBag, Sparkles, Mail } from 'lucide-react';
import { getSizeZoneColor } from '../../../../components/SizeRecommendPanel';
import { MOMENT_LABELS } from '../constants';

export const SessionEndedScreen = ({
  session,
  history,
  hardCart,
  sizeHistory,
  sendingEmail,
  emailSent,
  sessionEndReason,
  onAddToCart,
}) => {
  const getEndMessage = () => {
    switch (sessionEndReason) {
      case 'expired':
        return { title: 'Session Completed', subtitle: 'Your try-on session has ended', icon: '⏰' };
      case 'inactivity':
        return { title: 'Session Timed Out', subtitle: 'Your session ended due to inactivity', icon: '💤' };
      case 'quota_exhausted':
        return { title: "All Try-Ons Used! 🎉", subtitle: "You've explored all your looks for this session", icon: '✨' };
      default:
        return { title: 'Thank You!', subtitle: 'Your try-on session has ended', icon: '👋' };
    }
  };

  const endMessage = getEndMessage();
  const tryonResults = history.filter((h) => h.type === 'tryon');
  const momentResults = history.filter((h) => h.type === 'moment');
  const hasResults = tryonResults.length > 0 || momentResults.length > 0;

  const isInCart = (garment) => {
    if (!garment) return false;
    const productId = garment.product_id || garment.id;
    return hardCart.some((item) => (item.product_id || item.id) === productId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4 pb-24">
      <div className="max-w-2xl mx-auto">
        {session?.partner_logo_url && (
          <div className="text-center mb-4 pt-4">
            <img src={session.partner_logo_url} alt={session?.partner_name || 'Store'} className="h-12 mx-auto object-contain" />
          </div>
        )}

        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-lg flex items-center justify-center">
            <span className="text-4xl">{endMessage.icon}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-1">{endMessage.title}</h1>
          <p className="text-slate-600">{endMessage.subtitle}</p>
        </div>

        {session?.customer_email && hasResults && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
            {sendingEmail ? (
              <div className="flex items-center justify-center gap-3 text-blue-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-medium text-sm">Sending your looks to {session.customer_email}...</span>
              </div>
            ) : emailSent ? (
              <div className="flex items-center justify-center gap-3 text-green-600">
                <Check className="w-5 h-5" />
                <span className="font-medium text-sm">Your looks have been sent to {session.customer_email}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3 text-slate-500">
                <Mail className="w-5 h-5" />
                <span className="text-sm">Check your email for your try-on results</span>
              </div>
            )}
          </div>
        )}

        {tryonResults.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              Your Virtual Try-Ons ({tryonResults.length})
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {tryonResults.map((item, idx) => {
                const garment = item.garment;
                const price = garment?.selling_price || garment?.price || 0;
                const inCart = isInCart(garment);
                return (
                  <div key={item.id || idx} className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
                    <div className="aspect-[3/4] relative">
                      <img src={item.url} alt={garment?.product_name || `Look ${idx + 1}`} className="w-full h-full object-cover" />
                      {inCart && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          In Cart
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-slate-800 truncate mb-1">
                        {garment?.product_name || `Look ${idx + 1}`}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-blue-600 font-bold">₹{price.toLocaleString()}</span>
                        <button
                          onClick={() => onAddToCart(item)}
                          disabled={inCart}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${
                            inCart
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                          }`}
                        >
                          <ShoppingBag className="w-3 h-3" />
                          {inCart ? 'Added' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {momentResults.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              📸 Your Moments ({momentResults.length})
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {momentResults.map((item, idx) => {
                const label = MOMENT_LABELS[item.momentType] || item.momentType?.replace('_', ' ') || `Moment ${idx + 1}`;
                return (
                  <div key={item.id || idx} className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
                    <div className="aspect-[3/4]">
                      <img src={item.url} alt={label} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {Object.keys(sizeHistory).length > 0 && (
          <div className="mb-6" data-testid="size-history-section">
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              </svg>
              Your Size Guide ({Object.keys(sizeHistory).length} item{Object.keys(sizeHistory).length !== 1 ? 's' : ''})
            </h2>
            <div className="space-y-2">
              {Object.entries(sizeHistory).map(([pid, { result, garment: g }]) => {
                const conf = result.confidencePercent ?? 0;
                const effectiveConf = !result.photoSufficient && conf === 0 ? 5 : conf;
                const displayConf = !result.photoSufficient && conf === 0 ? '<10' : conf.toString();
                const ringColor = getSizeZoneColor(effectiveConf);
                const sizeFontSize = (result.recommendedSize || '').length > 3 ? 11 : (result.recommendedSize || '').length > 2 ? 14 : 18;
                return (
                  <div key={pid} data-testid={`size-history-item-${pid}`}
                    className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm border border-slate-100"
                  >
                    <img
                      src={g?.thumbnail_url || g?.primary_image_url || g?.images?.[0]?.image_url}
                      alt={g?.product_name || 'Product'}
                      className="w-12 h-16 object-cover rounded-lg flex-shrink-0 bg-slate-100"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{g?.product_name || g?.name || 'Product'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Show this to the store staff</p>
                      {!result.photoSufficient && (
                        <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                          Low photo confidence
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div style={{
                        width: 52, height: 52, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${ringColor}cc, ${ringColor})`,
                        color: '#fff', fontWeight: 900, fontSize: sizeFontSize,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 3px 10px ${ringColor}50`,
                        letterSpacing: '-0.02em',
                      }} data-testid={`size-history-badge-${pid}`}>
                        {result.recommendedSize}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: ringColor }}>{displayConf}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Share with store staff to pick up the right fit at the counter
            </p>
          </div>
        )}

        {hardCart.length > 0 && (
          <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-green-800 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Your Cart ({hardCart.length} item{hardCart.length !== 1 ? 's' : ''})
              </span>
              <span className="font-bold text-green-700">
                ₹{hardCart.reduce((sum, p) => sum + (p.selling_price || p.price || 0), 0).toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-green-700">Please proceed to the checkout counter to complete your purchase!</p>
          </div>
        )}

        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800 font-medium mb-1">Want to try on more?</p>
          <p className="text-sm text-blue-700">
            Scan the QR code at the store to start a new session and explore more styles!
          </p>
        </div>

        <p className="text-slate-400 text-sm text-center">
          Thank you for visiting {session?.partner_name || 'our store'}
        </p>
      </div>
    </div>
  );
};
